/** ConfirmDialog — 共通確認ダイアログユーティリティ。
 *  ShopScreen / CharacterScreen の重複 showConfirm を一元化。
 *  既存の .shop-confirm 系 CSS クラスをそのまま使用(style.css は変更しない)。
 *
 *  Usage:
 *    import { showConfirmDialog } from '../ConfirmDialog';
 *    const ok = await showConfirmDialog({ title: '購入確認', message: '...' });
 */

export interface ConfirmDialogOptions {
  readonly title: string;
  /** HTML 文字列を受け付ける(既存コードが innerHTML で渡していたため)。 */
  readonly message: string;
  readonly confirmLabel?: string; // default 'はい'
  readonly cancelLabel?: string;  // default 'いいえ'
}

/** Promise<boolean> を返す。確認=true / キャンセル=false。
 *  document.body 直下にモーダルをマウントし、閉じたら除去する。
 *  背景クリック / キャンセルボタンで false、確認ボタンで true を resolve。 */
export function showConfirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  const { title, message, confirmLabel = 'はい', cancelLabel = 'いいえ' } = options;

  return new Promise<boolean>((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'shop-confirm';
    backdrop.innerHTML = `
      <div class="shop-confirm-card">
        <div class="shop-confirm-title">${title}</div>
        <div class="shop-confirm-text">${message}</div>
        <div class="shop-confirm-actions">
          <button class="shop-confirm-yes" type="button">${confirmLabel}</button>
          <button class="shop-confirm-no" type="button">${cancelLabel}</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);

    const cleanup = (result: boolean): void => {
      document.body.removeChild(backdrop);
      resolve(result);
    };

    backdrop
      .querySelector<HTMLButtonElement>('.shop-confirm-yes')
      ?.addEventListener('click', () => cleanup(true));
    backdrop
      .querySelector<HTMLButtonElement>('.shop-confirm-no')
      ?.addEventListener('click', () => cleanup(false));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) cleanup(false);
    });
  });
}
